import ExcelJS from 'exceljs'
import {
  labelMes,
  buildVariacionUltimosMeses,
  monthsInRollingWindow,
  rollingWindowStartISO,
} from './seguimientoCesionesFacturacion'
import { nthBusinessDayOfMonth, parseISODate, toISODate, ymdKey } from './feriadosChile'

const NUM_MONTO = '_ * #,##0_ ;_ * -#,##0_ ;_ * "-"_ ;_ @_ '
const NUM_PCT = '0.0%'
const NUM_PCT_VAR = '0.00%'
const NUM_DIA = 'd-mmm'
const NUM_MES = 'mmm-yy'

const FONT = { name: 'Calibri', size: 11 }
const FONT_BOLD = { name: 'Calibri', size: 11, bold: true }
const FONT_TITLE = { name: 'Calibri', size: 14, bold: true }
const FILL_ORANGE = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFC000' },
}
const ALIGN_CENTER = { horizontal: 'center', vertical: 'middle', wrapText: true }

function localDateFromISO(iso) {
  const d = parseISODate(iso)
  if (!d) return null
  // ExcelJS date without UTC shift
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)
}

function monthStartDate(year, month) {
  return new Date(year, month - 1, 1, 12, 0, 0)
}

function lastDayOfMonthISO(year, month) {
  return toISODate(new Date(year, month, 0))
}

/** Serie diaria día a día dentro de la ventana móvil hasta fecha_corte. */
export function buildCalendarDaily(daily, fechaCorteISO) {
  const corte = parseISODate(fechaCorteISO)
  if (!corte) return []
  const desdeISO = rollingWindowStartISO(fechaCorteISO)
  const inicio = parseISODate(desdeISO)
  if (!inicio) return []
  const byFecha = new Map((daily || []).map((r) => [r.fecha, r]))
  const out = []
  const corteKey = ymdKey(corte)
  const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate())
  while (ymdKey(d) <= corteKey) {
    const iso = toISODate(d)
    const row = byFecha.get(iso)
    out.push({
      fecha: iso,
      cedIf: row?.cedIf || 0,
      cedOtro: row?.cedOtro || 0,
      facturacion: row?.facturacion || 0,
      cesion: row?.cesion || (row ? row.cedIf + row.cedOtro : 0),
    })
    d.setDate(d.getDate() + 1)
  }
  return out
}

function styleHeader(cell) {
  cell.font = FONT_BOLD
  cell.alignment = ALIGN_CENTER
}

function setMonto(cell, value) {
  cell.value = Number(value) || 0
  cell.numFmt = NUM_MONTO
  cell.font = FONT
}

function setFormula(cell, formula, result, numFmt) {
  cell.value = { formula, result: result == null ? null : result }
  if (numFmt) cell.numFmt = numFmt
  cell.font = FONT
}

/**
 * Exporta workbook con el mismo layout que el Excel de seguimiento:
 * bloque diario (cesiones | facturación) + AVANCE N días hábiles con fórmulas SUM / ratios / variación.
 */
export async function downloadSeguimientoCesionesExcel({
  daily,
  fechaCorte,
  nHabiles,
  avance,
}) {
  const calendar = buildCalendarDaily(daily, fechaCorte)
  if (!calendar.length) return

  const corte = parseISODate(fechaCorte)
  const n = Math.max(1, Math.floor(Number(nHabiles) || 1))

  const wb = new ExcelJS.Workbook()
  wb.creator = 'MGC'
  const ws = wb.addWorksheet('Hoja1', {
    views: [{ state: 'frozen', ySplit: 2 }],
  })

  ws.columns = [
    { key: 'A', width: 17.1 },
    { key: 'B', width: 14.6 },
    { key: 'C', width: 13 },
    { key: 'D', width: 15.6 },
    { key: 'E', width: 3 },
    { key: 'F', width: 8.6 },
    { key: 'G', width: 14.6 },
    { key: 'H', width: 13 },
    { key: 'I', width: 15.1 },
  ]

  // --- Encabezados (filas 1-2) ---
  ws.mergeCells('A1:A2')
  ws.mergeCells('B1:D1')
  ws.mergeCells('F1:F2')
  ws.mergeCells('G1:I1')

  ws.getCell('A1').value = 'DÍA'
  ws.getCell('B1').value = 'CESIONES'
  ws.getCell('B2').value = 'CESIONES IF'
  ws.getCell('C2').value = 'CESIONES OTROS'
  ws.getCell('D2').value = 'PARTICIPACIÓN IF'
  ws.getCell('F1').value = 'DÍA'
  ws.getCell('G1').value = 'FACTURACIÓN - CESIONES TOTALES'
  ws.getCell('G2').value = 'TOTAL FACTURACIÓN'
  ws.getCell('H2').value = 'TOTAL CESIONES'
  ws.getCell('I2').value = 'CESIONES / FACTURACIÓN'

  ;['A1', 'B1', 'B2', 'C2', 'D2', 'F1', 'G1', 'G2', 'H2', 'I2'].forEach((addr) =>
    styleHeader(ws.getCell(addr)),
  )
  ws.getRow(2).height = 28.8

  // --- Serie diaria (desde fila 3) ---
  const firstDataRow = 3
  const rowByFecha = new Map()

  calendar.forEach((day, idx) => {
    const r = firstDataRow + idx
    rowByFecha.set(day.fecha, r)
    const dateVal = localDateFromISO(day.fecha)

    ws.getCell(`A${r}`).value = dateVal
    ws.getCell(`A${r}`).numFmt = NUM_DIA
    ws.getCell(`A${r}`).font = FONT_BOLD

    setMonto(ws.getCell(`B${r}`), day.cedIf)
    setMonto(ws.getCell(`C${r}`), day.cedOtro)

    const part =
      day.cedIf + day.cedOtro > 0 ? day.cedIf / (day.cedIf + day.cedOtro) : 0
    setFormula(
      ws.getCell(`D${r}`),
      `IF((B${r}+C${r})=0,0,B${r}/(B${r}+C${r}))`,
      part,
      NUM_PCT,
    )

    ws.getCell(`F${r}`).value = dateVal
    ws.getCell(`F${r}`).numFmt = NUM_DIA
    ws.getCell(`F${r}`).font = FONT_BOLD

    setMonto(ws.getCell(`G${r}`), day.facturacion)

    const cesion = day.cesion || day.cedIf + day.cedOtro
    setFormula(ws.getCell(`H${r}`), `B${r}+C${r}`, cesion, NUM_MONTO)

    const ratio = day.facturacion > 0 ? cesion / day.facturacion : 0
    setFormula(
      ws.getCell(`I${r}`),
      `IF(G${r}=0,0,H${r}/G${r})`,
      ratio,
      NUM_PCT,
    )
  })

  const lastDataRow = firstDataRow + calendar.length - 1

  // --- Bloque AVANCE ---
  const titleRow = lastDataRow + 3
  const hdr1 = titleRow + 1
  const hdr2 = titleRow + 2
  const firstAvanceRow = titleRow + 3

  ws.mergeCells(`A${titleRow}:I${titleRow}`)
  const titleCell = ws.getCell(`A${titleRow}`)
  titleCell.value = `AVANCE ${n} DIAS HABILES de GESTION `
  titleCell.font = FONT_TITLE
  titleCell.fill = FILL_ORANGE
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
  ws.getRow(titleRow).height = 18

  ws.mergeCells(`A${hdr1}:A${hdr2}`)
  ws.mergeCells(`B${hdr1}:D${hdr1}`)
  ws.mergeCells(`F${hdr1}:F${hdr2}`)
  ws.mergeCells(`G${hdr1}:I${hdr1}`)

  ws.getCell(`B${hdr1}`).value = 'CESIONES'
  ws.getCell(`G${hdr1}`).value = 'FACTURACIÓN - CESIONES TOTALES'
  ws.getCell(`B${hdr2}`).value = 'CESIONES IF'
  ws.getCell(`C${hdr2}`).value = 'CESIONES OTROS'
  ws.getCell(`D${hdr2}`).value = 'PARTICIPACIÓN IF'
  ws.getCell(`G${hdr2}`).value = 'TOTAL FACTURACIÓN'
  ws.getCell(`H${hdr2}`).value = 'TOTAL CESIONES'
  ws.getCell(`I${hdr2}`).value = 'CESIONES / FACTURACIÓN'
  ;[`B${hdr1}`, `G${hdr1}`, `B${hdr2}`, `C${hdr2}`, `D${hdr2}`, `G${hdr2}`, `H${hdr2}`, `I${hdr2}`].forEach(
    (addr) => styleHeader(ws.getCell(addr)),
  )
  ws.getRow(hdr2).height = 28.8

  const avanceSource = avance?.length
    ? avance
    : monthsInRollingWindow(fechaCorte).map(({ year, month }) => {
        const endDate = nthBusinessDayOfMonth(year, month, n)
        let hasta = endDate ? toISODate(endDate) : lastDayOfMonthISO(year, month)
        if (hasta > fechaCorte) hasta = fechaCorte
        return {
          anio: year,
          mes: month,
          hasta,
          cedIf: 0,
          cedOtro: 0,
          facturacion: 0,
          cesion: 0,
          participacionIf: 0,
          cesionesSobreFacturacion: 0,
        }
      })

  const writtenMonths = []
  avanceSource.forEach((m) => {
    const month = m.mes
    const year = m.anio
    const desde = `${year}-${String(month).padStart(2, '0')}-01`
    let hasta = m.hasta
    let rFrom = rowByFecha.get(desde)
    let rTo = rowByFecha.get(hasta)
    // Si el día hábil de cierre no está en el calendario (p.ej. DST), usa el último día <= hasta.
    if (!rTo) {
      for (let i = calendar.length - 1; i >= 0; i -= 1) {
        if (calendar[i].fecha <= hasta) {
          hasta = calendar[i].fecha
          rTo = rowByFecha.get(hasta)
          break
        }
      }
    }
    if (!rFrom && calendar.length) {
      const firstIso = calendar.find((day) => day.fecha >= desde)?.fecha
      if (firstIso) rFrom = rowByFecha.get(firstIso)
    }
    if (!rFrom || !rTo) return

    const r = firstAvanceRow + writtenMonths.length
    writtenMonths.push(m)

    const mesDate = monthStartDate(year, month)
    ws.getCell(`A${r}`).value = mesDate
    ws.getCell(`A${r}`).numFmt = NUM_MES
    ws.getCell(`A${r}`).font = FONT_BOLD
    ws.getCell(`F${r}`).value = mesDate
    ws.getCell(`F${r}`).numFmt = NUM_MES
    ws.getCell(`F${r}`).font = FONT_BOLD

    setFormula(ws.getCell(`B${r}`), `SUM(B${rFrom}:B${rTo})`, m.cedIf, NUM_MONTO)
    setFormula(ws.getCell(`C${r}`), `SUM(C${rFrom}:C${rTo})`, m.cedOtro, NUM_MONTO)
    setFormula(
      ws.getCell(`D${r}`),
      `B${r}/(B${r}+C${r})`,
      m.participacionIf,
      NUM_PCT,
    )
    ws.getCell(`D${r}`).font = FONT_BOLD
    ws.getCell(`D${r}`).alignment = ALIGN_CENTER

    setFormula(ws.getCell(`G${r}`), `SUM(G${rFrom}:G${rTo})`, m.facturacion, NUM_MONTO)
    setFormula(ws.getCell(`H${r}`), `SUM(H${rFrom}:H${rTo})`, m.cesion, NUM_MONTO)
    setFormula(
      ws.getCell(`I${r}`),
      `H${r}/G${r}`,
      m.cesionesSobreFacturacion,
      NUM_PCT,
    )
    ws.getCell(`I${r}`).font = FONT_BOLD
    ws.getCell(`I${r}`).alignment = ALIGN_CENTER
  })

  const lastAvanceRow = firstAvanceRow + writtenMonths.length - 1
  if (writtenMonths.length >= 2) {
    const prev = lastAvanceRow - 1
    const curr = lastAvanceRow
    const varRow = lastAvanceRow + 1
    const varMeta = buildVariacionUltimosMeses(writtenMonths)
    const varLabel =
      varMeta?.label ||
      `Variacion % ${labelMes(writtenMonths[writtenMonths.length - 2].mes)}-${labelMes(
        writtenMonths[writtenMonths.length - 1].mes,
      )}`

    ws.getCell(`A${varRow}`).value = varLabel
    ws.getCell(`A${varRow}`).font = FONT_BOLD
    ws.getCell(`A${varRow}`).alignment = { horizontal: 'right' }
    ws.getCell(`F${varRow}`).value = varLabel
    ws.getCell(`F${varRow}`).font = FONT_BOLD
    ws.getCell(`F${varRow}`).alignment = { horizontal: 'right' }

    const varFormula = (col) => `(${col}${curr}-${col}${prev})/${col}${prev}`
    setFormula(ws.getCell(`B${varRow}`), varFormula('B'), varMeta?.cedIf ?? null, NUM_PCT_VAR)
    setFormula(ws.getCell(`C${varRow}`), varFormula('C'), varMeta?.cedOtro ?? null, NUM_PCT_VAR)
    setFormula(ws.getCell(`G${varRow}`), varFormula('G'), varMeta?.facturacion ?? null, NUM_PCT_VAR)
    setFormula(ws.getCell(`H${varRow}`), varFormula('H'), varMeta?.cesion ?? null, NUM_PCT_VAR)
    ;[`B${varRow}`, `C${varRow}`, `G${varRow}`, `H${varRow}`].forEach((addr) => {
      ws.getCell(addr).font = FONT_BOLD
      ws.getCell(addr).alignment = ALIGN_CENTER
    })
  }

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `CESIONES_FACTURACION_SEGUIMIENTO_${fechaCorte}_N${n}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
